import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { axiosInstance } from "../../utils/axiosInstance";
import { DropdownOption } from "cprime-foundational-components";

export const getGroupTypeApplicationsList = async () => {
  try {
    const response = await axiosInstance.post("api/groups/group-types-users");
    return response?.data;
  } catch (error) {
    return error;
  }
};

export const searchContactsList = async (url: any) => {
  try {
    const response = await axiosInstance.post(url);
    return response?.data;
  } catch (error) {
    return error;
  }
};

export const searchTokenRoleName = async (url: any) => {
  try {
    const response = await axiosInstance.get(url);
    return response?.data;
  } catch (error) {
    return error;
  }
};

export const getAssociateGroupsList = async (
  appId: number | string,
  excludeGroupId?: number | string,
) => {
  try {
    let url = `api/groups/list-for-association?appId=${appId}`;
    if (excludeGroupId) url += `&excludeGroupId=${excludeGroupId}`;
    const response = await axiosInstance.get(url);
    return response?.data;
  } catch (error) {
    return error;
  }
};

interface UserGroupForm {
  groupName: string;
  groupType: string;
  specifyGroupType?: string;
  distributionList: string;
  application: string;
  groupMembers: string[];
  primaryContact: string;
  new_group_members?: any;
  associateGroups?: (number | string)[];
  associatedRoles?: (number | string)[];
  usageType: DropdownOption[];
  attributeType?: { [key: number | string]: DropdownOption[] };
  attributeTypeValues?: { [key: number | string]: DropdownOption[] };
}

interface UserGroupState {
  form: UserGroupForm;
}

const initialUserGroupForm: UserGroupForm = {
  groupName: "",
  groupType: "",
  // specifyGroupType: "",
  distributionList: "",
  application: "",
  groupMembers: [],
  primaryContact: "",
  new_group_members: [],
  usageType: [],
  attributeType: {},
  attributeTypeValues: {},
};

const initialUserGroupState: UserGroupState = {
  form: initialUserGroupForm,
};

export const parseConditions = (formData: UserGroupForm) => {
  const { usageType, attributeType, attributeTypeValues } = formData;
  let conditions: {
    fieldName: string;
    operator: string;
    values: (string | number)[];
    andOrOperator: string;
    sequenceNo: number;
  }[] = [];
  if (usageType[0]?.label !== "Profile Approver") {
    return conditions;
  }

  for (
    let i = 0;
    i < (attributeType ? Object.keys(attributeType).length : 0);
    i++
  ) {
    const obj = {
      fieldName: attributeType?.[String(i)]?.[0].label ?? "",
      operator: "equal", // fixed operator as per the requirement
      values:
        attributeTypeValues?.[String(i)]?.map((value) => String(value?.id)) ??
        [],
      andOrOperator: "AND", // fixed andOrOperator as per the requirement
      sequenceNo: i + 1,
    };
    conditions.push(obj);
  }

  return conditions;
};

export const createUserGroup = async (formData: UserGroupForm, url: string) => {
  try {
    const payload = {
      group_name: formData.groupName,
      application: formData.application,
      group_type: formData.groupType,
      // specify_group_type: formData.specifyGroupType,
      dl: formData.distributionList,
      primary_contact: formData.primaryContact,
      group_members: formData.groupMembers,
      new_group_members: formData.new_group_members,
      associatedGroups: formData.associateGroups ?? [],
      associatedRoles: formData.associatedRoles ?? [],
      usage_type: formData?.usageType[0]?.label ?? [],
      conditions: parseConditions(formData),
    };

    const response = await axiosInstance.post(url, payload);
    return response?.data;
  } catch (error) {
    throw error;
  }
};

interface EditUserGroupPayload {
  group_name: string;
  group_type: string;
  specify_group_type?: string;
  dl: string;
  primary_contact: string;
  group_members: string[];
  new_group_members?: [];
  associatedGroups?: (number | string)[];
  associatedRoles?: (number | string)[];
}

export const editUserGroup = async (
  groupId: number,
  formData: EditUserGroupPayload,
  url: string,
) => {
  try {
    const response = await axiosInstance.put(url, formData);
    return response?.data;
  } catch (error) {
    throw error;
  }
};

const userGroupsSlice = createSlice({
  name: "userGroups",
  initialState: initialUserGroupState,
  reducers: {
    setGroupData(state, action: PayloadAction<Partial<UserGroupForm>>) {
      state.form = { ...state.form, ...action.payload };
    },
    resetGroupData(state) {
      state.form = initialUserGroupForm;
    },
  },
});

export const { setGroupData, resetGroupData } = userGroupsSlice.actions;
export default userGroupsSlice.reducer;
